import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// ###### SIMPLE CUSTOM SCROLLBAR
const ScrollbarNavigation = ({ years, tripsByYear, sortDescending, containerRef }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const scrollbarRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const hideTimerRef = useRef(null);
  const scrollDebounceRef = useRef(null);

  // ###### MOBILE AND FULL WIDTH DETECTION
  useEffect(() => {
    const checkLayout = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
      
      // Check if container is full width (no max-width constraint)
      if (containerRef.current) {
        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        const viewportWidth = window.innerWidth;
        
        // Consider full width if container width is close to viewport width (accounting for padding)
        setIsFullWidth(containerWidth >= viewportWidth - 32); // 32px for padding
      }
    };
    
    checkLayout();
    window.addEventListener('resize', checkLayout);
    
    return () => window.removeEventListener('resize', checkLayout);
  }, [containerRef]);

  // ###### SCROLL DETECTION
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;

      // Show scrollbar when content is scrollable
      setIsVisible(scrollHeight > containerHeight);

      // Calculate scroll progress (0 to 1)
      const maxScroll = scrollHeight - containerHeight;
      const progress = maxScroll > 0 ? scrollTop / maxScroll : 0;
      setScrollProgress(progress);

      // Show scrollbar temporarily when scrolling (only for internal)
      if (isFullWidth) {
        setIsInteracting(true);
        
        // Debounce timer reset to avoid excessive resets
        if (scrollDebounceRef.current) {
          clearTimeout(scrollDebounceRef.current);
        }
        
        scrollDebounceRef.current = setTimeout(() => {
          resetHideTimer();
        }, 100); // Wait 100ms after scroll stops
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);

      {!isFullWidth && (
        handleScroll() // Initial call only if not full width
      )}

      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [isFullWidth]);

  // ###### SCROLL TO POSITION
  const scrollToPosition = (progress) => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const maxScroll = container.scrollHeight - container.clientHeight;
    const targetScroll = progress * maxScroll;
    
    container.scrollTop = targetScroll;
  };

  // ###### GET CLIENT Y (MOUSE OR TOUCH)
  const getClientY = (e) => {
    return e.touches ? e.touches[0].clientY : e.clientY;
  };

  // ###### MOUSE/TOUCH EVENTS
  const handleStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    showScrollbar();
  };

  const handleMove = (e) => {
    if (!isDragging || !scrollbarRef.current) return;
    
    const rect = scrollbarRef.current.getBoundingClientRect();
    const y = getClientY(e) - rect.top;
    const progress = Math.max(0, Math.min(1, y / rect.height));
    
    scrollToPosition(progress);
  };

  const handleEnd = () => {
    setIsDragging(false);
    if (isFullWidth) {
      resetHideTimer();
    }
  };

  // ###### CLICK/TAP ON TRACK
  const handleTrackClick = (e) => {
    const rect = scrollbarRef.current.getBoundingClientRect();
    const y = getClientY(e) - rect.top;
    const progress = Math.max(0, Math.min(1, y / rect.height));
    
    scrollToPosition(progress);
    showScrollbar();
    resetHideTimer();
  };

  // ###### EVENT LISTENERS
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging]);

  // ###### SCROLL TO YEAR
  const scrollToYear = (year) => {
    if (!containerRef.current) return;
    
    const yearElement = containerRef.current.querySelector(`[data-year="${year}"]`);
    if (yearElement) {
      yearElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  // ###### GET YEAR POSITION ON SCROLLBAR
  const getYearPosition = (year) => {
    if (!containerRef.current) return 0;
    
    const yearElement = containerRef.current.querySelector(`[data-year="${year}"]`);
    if (!yearElement) return 0;
    
    const container = containerRef.current;
    const yearTop = yearElement.offsetTop;
    const containerHeight = container.clientHeight;
    const scrollHeight = container.scrollHeight;
    
    // Position as percentage of total scrollable height
    const position = yearTop / (scrollHeight - containerHeight);
    
    return Math.max(0, Math.min(1, position));
  };

  // ###### DYNAMIC SCROLLBAR VISIBILITY
  const showScrollbar = () => {
    if (!isFullWidth) return; // Only for internal scrollbar
    
    setIsHovered(true);
    setIsInteracting(true);
    
    // Clear existing timer
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
  };

  const hideScrollbar = () => {
    if (!isFullWidth) return; // Only for internal scrollbar
    
    setIsHovered(false);
    
    // Set timer to hide after interaction ends
    hideTimerRef.current = setTimeout(() => {
      setIsInteracting(false);
    }, 700); // Hide after 700ms of no interaction
  };

  const resetHideTimer = () => {
    if (!isFullWidth) return; // Only for internal scrollbar
    
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    
    hideTimerRef.current = setTimeout(() => {
      setIsInteracting(false);
    }, 700);
  };

  // ###### MANUAL HIDE FUNCTION
  const handleManualHide = (e) => {
    e.stopPropagation();
    setIsInteracting(false);
    setIsHovered(false);
    
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
  };

  // ###### CLEANUP TIMERS ON UNMOUNT
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }
    };
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Responsive Scrollbar */}
      <div 
        ref={scrollbarRef}
        className={`absolute top-1/2 transform -translate-y-1/2 z-30 transition-opacity duration-300 ${
          isFullWidth 
            ? (isMobile 
                ? 'left-2 h-[60vh]' 
                : 'left-2 h-[70vh]')
            : (isMobile 
                ? '-left-12 h-[60vh]' 
                : '-left-16 h-[70vh]')
        } ${
          isFullWidth && !isInteracting && !isHovered 
            ? 'opacity-0 pointer-events-none' 
            : 'opacity-100'
        }`}
        onMouseEnter={showScrollbar}
        onMouseLeave={hideScrollbar}
        onTouchStart={showScrollbar}
      >
        {/* Background Container */}
        <div className="bg-white bg-opacity-70 rounded-lg px-4 py-2 h-full flex flex-col items-center justify-between">
          {/* Scroll Track */}
          <div 
            className={`bg-gray-200 rounded-full flex-1 cursor-pointer relative ${
              isMobile ? 'w-1.5' : 'w-2'
            }`}
            onClick={handleTrackClick}
            onTouchStart={handleStart}
          >
          {/* Year Markers */}
          {years.map((year) => {
            const position = getYearPosition(year);
            return (
              <div
                key={year}
                className="absolute w-2 h-2 -left-0.5 transform -translate-y-1/2 cursor-pointer"
                style={{ top: `${position * 100}%` }}
                onClick={(e) => {
                  e.stopPropagation();
                  scrollToYear(year);
                }}
              >
                {/* Year Marker Circle */}
                <div className="w-2 h-2 rounded-full bg-gray-600 hover:bg-gray-800 transition-colors duration-200" />
                
                {/* Year Label */}
                <div className="absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <div className="bg-gray-800 bg-opacity-80 text-gray-100 text-[10px] font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap">
                    {year}
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Slider */}
          <div
            className={`absolute bg-gray-600 rounded-full border-2 border-white shadow-lg cursor-grab active:cursor-grabbing transform -translate-y-1/2 ${
              isMobile 
                ? 'w-5 h-5 -translate-x-1.5' 
                : 'w-5 h-5 -translate-x-1.5'
            }`}
            style={{ top: `${scrollProgress * 100}%` }}
            onMouseDown={handleStart}
            onTouchStart={handleStart}
          />
          </div>
          
          {/* Close Button - Only for internal scrollbar */}
          {isFullWidth && (
            <button
              onClick={handleManualHide}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200 mt-1 p-1"
              title="Fechar barra de rolagem"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ScrollbarNavigation;